import {
  LifeCycleState,
  TradeInPreviewAcquisition
} from '@carvana/merch-contracts';
import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import COOKIES from '~/constants/cookies';
import QueryParams from '~/constants/queryParams';
import { DeepPartial } from '~/types';
import { BrowserCookies } from '~/utilities';

function getActiveAcquisition(trades: TradeInPreviewAcquisition[]) {
  return trades.find(
    (a: TradeInPreviewAcquisition) =>
      a.lifeCycleState === LifeCycleState.Active ||
      a.lifeCycleState === LifeCycleState.Scheduled ||
      a.lifeCycleState === LifeCycleState.AppliedToPurchase
  );
}

export type ContextType = {
  activeTrade: TradeInPreviewAcquisition | null;
  initialPreferredAcquisitionName?: string;
  isTradeInApplied: boolean;
  preferredAcquisitionNameFromQueryParam?: string;
  selectedTrade: TradeInPreviewAcquisition | null;
  setActiveTrade: (trade: TradeInPreviewAcquisition | null) => void;
  setSelectedTrade: (trade: TradeInPreviewAcquisition | null) => void;
  setTradeInApplied: Dispatch<SetStateAction<boolean>>;
  toggleAppliedTrade: () => void;
  trades: LoadableState<TradeInPreviewAcquisition[]>;
};

export const defaultContext: ContextType = {
  activeTrade: null,
  initialPreferredAcquisitionName: undefined,
  isTradeInApplied: true,
  preferredAcquisitionNameFromQueryParam: undefined,
  selectedTrade: null,
  setActiveTrade() {},
  setSelectedTrade() {},
  setTradeInApplied() {},
  toggleAppliedTrade() {},
  trades: defaultLoadableState<TradeInPreviewAcquisition[]>([])
};

const TradeContext = createContext(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  isTradeInApplied?: boolean;
  preferredAcquisitionName?: string;
  preferredAcquisitionNameFromQueryParam?: string;
}>;

export function TradeContextProvider(props: OwnProps) {
  const [activeTrade, setActiveTrade] = useState(defaultContext.activeTrade);
  const [selectedTrade, setSelectedTrade] = useState(
    defaultContext.selectedTrade
  );
  const [isTradeInApplied, setTradeInApplied] = useState(
    props.isTradeInApplied ?? true
  );
  const trades = useLoadableState<TradeInPreviewAcquisition[]>([]);

  useEffect(() => {
    setActiveTrade(getActiveAcquisition(trades.data) ?? null);
  }, [trades.data]);

  const hasQueryParam = props.preferredAcquisitionNameFromQueryParam != null;

  // Keyed on the param value (not run-once) so a client-side navigation to a new
  // ?preferredAcquisitionName also strips the URL and clears the applied cookie.
  // The searchParams.has() check keeps it idempotent: if the param was already
  // stripped (e.g. a back-navigation cached replay), we skip so a manual trade
  // toggle isn't overridden.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!hasQueryParam) return;

    const url = new URL(window.location.href);

    if (!url.searchParams.has(QueryParams.preferredAcquisitionName)) return;

    BrowserCookies.remove(COOKIES.tradeInApplied);
    url.searchParams.delete(QueryParams.preferredAcquisitionName);
    window.history.replaceState({}, '', url);
  }, [hasQueryParam, props.preferredAcquisitionNameFromQueryParam]);

  const toggleAppliedTrade = useCallback(() => {
    setTradeInApplied(v => !v);
  }, []);

  const value = useMemo<ContextType>(
    () => ({
      activeTrade,
      initialPreferredAcquisitionName: props.preferredAcquisitionName,
      isTradeInApplied,
      preferredAcquisitionNameFromQueryParam:
        props.preferredAcquisitionNameFromQueryParam,
      selectedTrade,
      setActiveTrade,
      setSelectedTrade,
      setTradeInApplied,
      toggleAppliedTrade,
      trades
    }),
    [
      activeTrade,
      isTradeInApplied,
      props.preferredAcquisitionName,
      props.preferredAcquisitionNameFromQueryParam,
      selectedTrade,
      toggleAppliedTrade,
      trades
    ]
  );

  return (
    <TradeContext.Provider value={value}>
      {props.children}
    </TradeContext.Provider>
  );
}

TradeContextProvider.displayName = 'TradeContextProvider';

export const generateTradeContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function MockedTradeContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <TradeContext.Provider value={generateTradeContext(mockValue)}>
      {children}
    </TradeContext.Provider>
  );
}

export function useGetTradeInStatus(): {
  isActive: boolean;
  isExpired: boolean;
  isPending: boolean;
} {
  const { lifeCycleState } = useContext(TradeContext)?.selectedTrade ?? {};

  return useMemo(
    () => ({
      isActive:
        lifeCycleState === LifeCycleState.Active ||
        lifeCycleState === LifeCycleState.Scheduled ||
        lifeCycleState === LifeCycleState.AppliedToPurchase,
      isExpired: lifeCycleState === LifeCycleState.Expired,
      isPending:
        lifeCycleState === LifeCycleState.Manual ||
        lifeCycleState === LifeCycleState.Rejected
    }),
    [lifeCycleState]
  );
}

export function useActiveAppliedTrade() {
  const { isTradeInApplied, selectedTrade } = useContext(TradeContext);
  const { isActive } = useGetTradeInStatus();

  return isTradeInApplied && isActive && selectedTrade;
}

export function useIsTradeInApplied() {
  return useContext(TradeContext).isTradeInApplied;
}

export function useHasTrades() {
  return useContext(TradeContext).trades.data.length > 0;
}

export function useIsTradeLoaded() {
  return useContext(TradeContext).trades.isLoaded;
}

export function useSelectedTrade() {
  return useContext(TradeContext).selectedTrade;
}

export function useGetAcquisitionNameForApiRequest() {
  const {
    initialPreferredAcquisitionName,
    isTradeInApplied,
    selectedTrade,
    trades: { data: trades, hasEverLoaded }
  } = useContext(TradeContext);

  if (!isTradeInApplied) return '';

  if (!hasEverLoaded) return initialPreferredAcquisitionName ?? '';

  return trades.length <= 1 ? '' : selectedTrade?.id ?? '';
}

export default TradeContext;
